/** Default print rate in inches per second (`^PR`). */
export const DEFAULT_ZPL_PRINT_SPEED_IPS = 2;

/** Default relative media darkness (`^MD`, Zebra Δ). */
export const DEFAULT_ZPL_MEDIA_DARKNESS_DELTA = -8;

export type ZplPrintSettings = {
  speedIps: number;
  darknessDelta: number;
};

const PR_COMMAND_RE = /\^PR[^\r\n^]*/gi;
const MD_COMMAND_RE = /\^MD-?\d+/gi;
const PR_CAPTURE_RE = /\^PR[ \t]*(\d+)/i;
const MD_CAPTURE_RE = /\^MD[ \t]*(-?\d+)/i;
const STANDALONE_PR_MD_LINE_RE = /^[ \t]*\^(?:PR[^\r\n^]*|MD-?\d+)[ \t]*\r?\n?/gim;
const HANDLEBARS_COMMENT_RE = /\{\{!--[\s\S]*?--\}\}/g;
const ZPL_COMMENT_LINE_RE = /^[ \t]*\^FX[^\r\n]*$/gim;

function clampSpeedIps(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_ZPL_PRINT_SPEED_IPS;
  return Math.min(12, Math.max(1, Math.round(value)));
}

function clampDarknessDelta(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_ZPL_MEDIA_DARKNESS_DELTA;
  return Math.min(30, Math.max(-30, Math.round(value)));
}

function stripTemplateCommentsForSettingsParse(zpl: string): string {
  return zpl.replace(HANDLEBARS_COMMENT_RE, "").replace(ZPL_COMMENT_LINE_RE, "");
}

/** Extracts explicit `^PR` / `^MD` values present in a ZPL or Handlebars template string. */
export function extractZplPrintSettings(zpl: string): Partial<ZplPrintSettings> {
  const searchable = stripTemplateCommentsForSettingsParse(zpl);
  const out: Partial<ZplPrintSettings> = {};
  const prMatch = PR_CAPTURE_RE.exec(searchable);
  if (prMatch != null) {
    out.speedIps = clampSpeedIps(Number(prMatch[1]));
  }
  const mdMatch = MD_CAPTURE_RE.exec(searchable);
  if (mdMatch != null) {
    out.darknessDelta = clampDarknessDelta(Number(mdMatch[1]));
  }
  return out;
}

/** Parses `^PR` / `^MD` from a ZPL or Handlebars template string, falling back to defaults. */
export function parseZplPrintSettingsFromZpl(zpl: string): ZplPrintSettings {
  return normalizeZplPrintSettings(extractZplPrintSettings(zpl));
}

export function normalizeZplPrintSettings(input: {
  speedIps?: unknown;
  darknessDelta?: unknown;
}): ZplPrintSettings {
  const speedRaw = typeof input.speedIps === "number" ? input.speedIps : Number(input.speedIps);
  const darknessRaw =
    typeof input.darknessDelta === "number" ? input.darknessDelta : Number(input.darknessDelta);
  return {
    speedIps: clampSpeedIps(speedRaw),
    darknessDelta: clampDarknessDelta(darknessRaw),
  };
}

function parseBodyNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value.trim());
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

export function parseZplPrintSettingsFromBody(
  body: {
    speed_ips?: unknown;
    darkness_delta?: unknown;
  } | null | undefined,
  fallback?: Partial<ZplPrintSettings>,
): ZplPrintSettings {
  return normalizeZplPrintSettings({
    speedIps: parseBodyNumber(body?.speed_ips) ?? fallback?.speedIps ?? DEFAULT_ZPL_PRINT_SPEED_IPS,
    darknessDelta:
      parseBodyNumber(body?.darkness_delta) ??
      fallback?.darknessDelta ??
      DEFAULT_ZPL_MEDIA_DARKNESS_DELTA,
  });
}

/** Inserts `^PR` / `^MD` after `^XA`, replacing any existing copies and preserving leading template comments. */
export function applyZplPrintSettings(
  zpl: string,
  settings: Partial<ZplPrintSettings> = {},
): string {
  const embedded = extractZplPrintSettings(zpl);
  const { speedIps, darknessDelta } = normalizeZplPrintSettings({
    speedIps: settings.speedIps !== undefined ? settings.speedIps : embedded.speedIps,
    darknessDelta:
      settings.darknessDelta !== undefined ? settings.darknessDelta : embedded.darknessDelta,
  });
  const trimmed = zpl.replace(/^\uFEFF/, "").trimStart();
  const withoutStandalone = trimmed.replace(STANDALONE_PR_MD_LINE_RE, "");
  const without = withoutStandalone.replace(PR_COMMAND_RE, "").replace(MD_COMMAND_RE, "");
  const commands = `^PR${String(speedIps)}\n^MD${String(darknessDelta)}\n`;
  if (/^\^XA/im.test(without)) {
    return without.replace(/^\^XA[ \t]*\r?\n?/im, `^XA\n${commands}`);
  }
  if (/\^XA/i.test(without)) {
    return without.replace(/\^XA[ \t]*\r?\n?/i, `^XA\n${commands}`);
  }
  return `^XA\n${commands}${without}`;
}

const PQ_COMMAND_RE = /\^PQ[^\n^]*/gi;

export function normalizeZplPrintQuantity(value: unknown): number {
  const raw = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(raw)) return 1;
  return Math.min(99, Math.max(1, Math.round(raw)));
}

/** Inserts `^PQ` before `^XZ`, replacing any existing print-quantity command. */
export function applyZplPrintQuantity(zpl: string, quantity: unknown): string {
  const copies = normalizeZplPrintQuantity(quantity);
  const without = zpl.replace(PQ_COMMAND_RE, "");
  if (/\^XZ/i.test(without)) {
    return without.replace(/\^XZ/i, `^PQ${String(copies)}\n^XZ`);
  }
  return `${without.trimEnd()}\n^PQ${String(copies)}\n^XZ`;
}
